/**
 * industryRecommendedApps.ts — deterministic industry → ranked-apps map for
 * the connect-apps step (2026-08-19 owner directive: recommendations must be
 * strategic PER INDUSTRY across all 18 work areas, not 8 regex buckets).
 *
 * This is the INSTANT/fallback layer: the model-ranked
 * `catalogRecommendations` from getOnboardingRecommendations wins when
 * present; these lists cover the pre-recs window (new-org drafts before the
 * legal-name confirm, organic signups mid-research) and every failure path.
 *
 * Keys are the DEFAULT_PRACTICE_OPTIONS labels (src/data/onboardingOptions.ts)
 * — the exact chips the user taps at profile setup. Order IS the rank.
 * Never slack / microsoft_teams (owner directive 2026-08-12: group threads on
 * the platform are the communication surface).
 */

export const INDUSTRY_RECOMMENDED_APPS: Record<string, string[]> = {
  // 12 per industry (2026-08-23 owner directive: the catalog's "recommended
  // for you" group is a TOP 12). Positions 1-8 keep the 2026-08-20
  // adoption-evidence ranking; 9-12 extend it with the industry's
  // next-most-adopted tools (Okta Businesses at Work cross-checks: Microsoft
  // 365/Google Workspace lead everywhere, Zoom/DocuSign/QuickBooks/HubSpot
  // dominate their niches).
  // Legal: Outlook/Microsoft 365 is the #1 daily system in law firms (ABA
  // TechReport; Fernando's firm is an Outlook shop), DocuSign is the
  // e-signature default (~42% US share), then storage + billing.
  Legal: ['outlook', 'gmail', 'googlecalendar', 'googledrive', 'docusign', 'dropbox', 'googledocs', 'quickbooks', 'calendly', 'notion', 'googlesheets', 'zoom'],
  // Financial: QuickBooks (62% SMB accounting share) and Outlook lead;
  // Xero is the #2 accounting package; Salesforce is the #1 advisor CRM;
  // DocuSign signs the account paperwork (2026 T3/Inside Information survey).
  'Finance & Accounting': ['quickbooks', 'outlook', 'gmail', 'googlesheets', 'xero', 'stripe', 'docusign', 'salesforce', 'googlecalendar', 'googledrive', 'calendly', 'freshbooks'],
  'Marketing & Content': ['gmail', 'canva', 'mailchimp', 'googledrive', 'notion', 'linkedin', 'figma', 'googledocs', 'googlecalendar', 'youtube', 'googlesheets', 'typeform'],
  'Sales & Business Development': ['hubspot', 'gmail', 'salesforce', 'googlecalendar', 'calendly', 'linkedin', 'googlesheets', 'zoom', 'googledrive', 'docusign', 'notion', 'stripe'],
  'Operations & Admin': ['gmail', 'googlecalendar', 'googlesheets', 'googledrive', 'asana', 'notion', 'quickbooks', 'monday', 'googledocs', 'trello', 'calendly', 'docusign'],
  'Customer Support': ['zendesk', 'intercom', 'gmail', 'whatsapp', 'notion', 'googlesheets', 'trello', 'googledrive', 'googlecalendar', 'typeform', 'asana', 'googledocs'],
  'Real Estate': ['gmail', 'googlecalendar', 'docusign', 'googledrive', 'calendly', 'whatsapp', 'hubspot', 'dropbox', 'googlesheets', 'canva', 'outlook', 'quickbooks'],
  Healthcare: ['gmail', 'googlecalendar', 'calendly', 'googledrive', 'zoom', 'googlesheets', 'typeform', 'dropbox', 'googledocs', 'outlook', 'quickbooks', 'notion'],
  'Recruiting & HR': ['gmail', 'linkedin', 'googlecalendar', 'calendly', 'notion', 'googlesheets', 'typeform', 'zoom', 'googledrive', 'googledocs', 'asana', 'docusign'],
  'Consulting & Professional Services': ['gmail', 'googlecalendar', 'googledrive', 'googledocs', 'notion', 'calendly', 'quickbooks', 'zoom', 'googlesheets', 'docusign', 'asana', 'hubspot'],
  'Software & Product': ['github', 'linear', 'jira', 'notion', 'figma', 'gitlab', 'googledrive', 'gmail', 'googlecalendar', 'miro', 'googlesheets', 'clickup'],
  'E-commerce & Retail': ['shopify', 'stripe', 'gmail', 'googlesheets', 'mailchimp', 'canva', 'quickbooks', 'whatsapp', 'googledrive', 'googlecalendar', 'typeform', 'notion'],
  'Education & Training': ['gmail', 'googlecalendar', 'googledrive', 'googledocs', 'zoom', 'canva', 'typeform', 'googlesheets', 'notion', 'calendly', 'youtube', 'mailchimp'],
  Insurance: ['gmail', 'outlook', 'googlecalendar', 'googledrive', 'googlesheets', 'docusign', 'calendly', 'quickbooks', 'hubspot', 'whatsapp', 'googledocs', 'typeform'],
  'Construction & Trades': ['gmail', 'googlesheets', 'googledrive', 'quickbooks', 'whatsapp', 'googlecalendar', 'dropbox', 'monday', 'googledocs', 'docusign', 'asana', 'outlook'],
  'Media & Creative': ['canva', 'figma', 'googledrive', 'gmail', 'notion', 'dropbox', 'miro', 'mailchimp', 'youtube', 'googlecalendar', 'googledocs', 'linkedin'],
  'Nonprofit & Government': ['gmail', 'googledrive', 'googledocs', 'googlesheets', 'googlecalendar', 'mailchimp', 'typeform', 'canva', 'zoom', 'notion', 'quickbooks', 'docusign'],
  Other: ['gmail', 'googlecalendar', 'googledrive', 'googledocs', 'googlesheets', 'notion', 'quickbooks', 'calendly', 'outlook', 'dropbox', 'docusign', 'zoom'],
};

/**
 * Free-text fallback for research-derived firm profiles whose industry is a
 * sentence, not a chip ("boutique immigration law firm in Austin"). First
 * matching rule wins. Ported from the old CATALOG_RECOMMENDED_RULES with the
 * never-slack rule applied.
 */
export const INDUSTRY_TEXT_RULES: Array<[RegExp, string[]]> = [
  [/law|legal|attorney|litigat|paralegal|counsel/i, INDUSTRY_RECOMMENDED_APPS.Legal],
  // Before real-estate: "insurance brokerage" must not fall to the
  // real-estate rule's "brokerage".
  [/insur/i, INDUSTRY_RECOMMENDED_APPS.Insurance],
  [/real estate|realty|propert|brokerage|title/i, INDUSTRY_RECOMMENDED_APPS['Real Estate']],
  [/construct|contractor|builder|roofing|plumb|hvac|electric/i, INDUSTRY_RECOMMENDED_APPS['Construction & Trades']],
  [/health|medical|clinic|dental|therap|wellness/i, INDUSTRY_RECOMMENDED_APPS.Healthcare],
  [/financ|account|tax|bookkeep|wealth|lend/i, INDUSTRY_RECOMMENDED_APPS['Finance & Accounting']],
  [/recruit|staffing|talent|\bhr\b|human resources/i, INDUSTRY_RECOMMENDED_APPS['Recruiting & HR']],
  [/market|agency|advertis|brand|content|social media/i, INDUSTRY_RECOMMENDED_APPS['Marketing & Content']],
  [/media|creative|design|studio|video|photo/i, INDUSTRY_RECOMMENDED_APPS['Media & Creative']],
  [/retail|commerce|shop|store|merchan/i, INDUSTRY_RECOMMENDED_APPS['E-commerce & Retail']],
  [/support|helpdesk|service desk|customer success/i, INDUSTRY_RECOMMENDED_APPS['Customer Support']],
  [/sales|business development|\bbd\b|crm/i, INDUSTRY_RECOMMENDED_APPS['Sales & Business Development']],
  [/school|educat|training|tutor|course|academ/i, INDUSTRY_RECOMMENDED_APPS['Education & Training']],
  [/nonprofit|non-profit|charit|government|municipal|public sector/i, INDUSTRY_RECOMMENDED_APPS['Nonprofit & Government']],
  [/consult|advisor|professional services/i, INDUSTRY_RECOMMENDED_APPS['Consulting & Professional Services']],
  [/tech|software|saas|startup|engineer|develop|product/i, INDUSTRY_RECOMMENDED_APPS['Software & Product']],
];

export const CATALOG_RECOMMENDED_DEFAULT = INDUSTRY_RECOMMENDED_APPS.Other;

/**
 * Resolve the instant ranked list: the user's FIRST selected work-area chip
 * with a list wins (they told us their industry — believe them), then the
 * free-text rules over the research haystack, then the general default.
 */
export function rankedIndustryApps(selectedAreas: string[], haystack: string): string[] {
  // 'Other' only wins when it's the only signal — any concrete industry chip
  // beats it regardless of tap order.
  for (const area of selectedAreas) {
    if (area === 'Other') continue;
    const list = INDUSTRY_RECOMMENDED_APPS[area];
    if (list) return list;
  }
  const rule = INDUSTRY_TEXT_RULES.find(([re]) => re.test(haystack));
  if (rule) return rule[1];
  return CATALOG_RECOMMENDED_DEFAULT;
}
